/**
 * Schémas Zod — Missing Information.
 *
 * Source DB : `app.missing_information` (migration 0021, audit trigger câblé 0021:553).
 *
 * Lecture seule depuis 9.F. Chunk 9.I.1 ajoute la transition `fulfill`
 * (`open|in_progress → fulfilled`). Waive/Reopen/Supersede = dette P3 9.I.2.
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

// -----------------------------------------------------------------------------
// Enums (miroir DB)
// -----------------------------------------------------------------------------

export const MissingInfoStatusSchema = z.enum([
  "open",
  "in_progress",
  "fulfilled",
  "waived",
  "superseded",
]);
export type MissingInfoStatus = z.infer<typeof MissingInfoStatusSchema>;

export const MissingInfoAudienceSchema = z.enum(["client", "broker", "admin", "insurer"]);
export type MissingInfoAudience = z.infer<typeof MissingInfoAudienceSchema>;

// -----------------------------------------------------------------------------
// Row (lecture)
// -----------------------------------------------------------------------------

export const MissingInformationRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,

  // Scope polymorphe — exactement un non-null en DB (CHECK).
  scope_intake_event_id: UuidSchema.nullable(),
  scope_quote_request_id: UuidSchema.nullable(),
  scope_submission_id: UuidSchema.nullable(),
  scope_project_id: UuidSchema.nullable(),

  required_by_template_id: UuidSchema.nullable(),
  /**
   * 9.BK.1 — le requis de dossier dont cet item est la matérialisation, ou
   * `null` pour une demande ponctuelle.
   *
   * Il voyage au fil parce que la surface opérationnelle en a besoin : un
   * requis satisfait puis redevenu non satisfait laisse une ligne close et une
   * ligne ouverte — c'est voulu, le modèle garde la trace — et seul cet
   * identifiant permet de savoir qu'elles décrivent la même exigence.
   */
  requirement_id: UuidSchema.nullable(),
  field_path: z.string(),
  field_label_fr: z.string().nullable(),
  field_label_en: z.string().nullable(),
  details: z.record(z.unknown()).nullable(),
  question_audience: MissingInfoAudienceSchema.nullable(),

  status: MissingInfoStatusSchema,
  fulfilled_value: z.unknown().nullable(),
  fulfilled_at: z.string().nullable(),
  fulfilled_by_user: UuidSchema.nullable(),
  fulfilled_by_agent: UuidSchema.nullable(),

  // 9.M.7 — Forensic visible (variant `fulfilled`).
  fulfilled_by_agent_display_name: z.string().nullable().optional(),

  waiver_reason: z.string().nullable(),
  due_at: z.string().nullable(),

  created_at: z.string(),
  updated_at: z.string(),
  created_by_user: UuidSchema.nullable(),
  created_by_agent: UuidSchema.nullable(),

  // 9.M.7 — Forensic visible (variant `created`).
  created_by_agent_display_name: z.string().nullable().optional(),

  deleted_at: z.string().nullable(),
});
export type MissingInformationRow = z.infer<typeof MissingInformationRowSchema>;

// -----------------------------------------------------------------------------
// Filtres listing — GET /v1/missing-information
// -----------------------------------------------------------------------------

export const MissingInformationFilterSchema = z.object({
  project_id: UuidSchema.optional(),
  submission_id: UuidSchema.optional(),
  quote_request_id: UuidSchema.optional(),
  intake_event_id: UuidSchema.optional(),
  status: MissingInfoStatusSchema.optional(),
  audience: MissingInfoAudienceSchema.optional(),
});
export type MissingInformationFilter = z.infer<typeof MissingInformationFilterSchema>;

// -----------------------------------------------------------------------------
// Fulfill (Chunk 9.I.1)
// -----------------------------------------------------------------------------
//
// Décisions PO 9.I.1 :
//   Q2=fulfill only (Phase 1 — pas de waive/reopen/supersede).
//
// `fulfilled_value` est un jsonb polymorphe côté DB (peut tenir string,
// number, boolean, object, array — selon le `field_path` du ticket MI).
// On accepte ces 5 shapes via union explicite ; null/undefined rejetés
// (semantically "no answer" ≠ "fulfilled"). `string().min(1)` empêche
// les empty-string answers (idem semantically).
//
// Pas de `notes` Phase 1 — la table `app.missing_information` n'a pas de
// colonne dédiée notes ; si un broker veut commenter, il peut inclure le
// commentaire dans `fulfilled_value` (forme `{ value: ..., note: ... }`).

const FulfilledValueSchema = z.union([
  z.string().min(1),
  z.number(),
  z.boolean(),
  z.record(z.unknown()),
  z.array(z.unknown()),
]);

/**
 * Body PATCH `/v1/missing-information/:id/fulfill` — broker-facing.
 *
 * Transitionne `status` `open|in_progress → fulfilled` + persiste
 * `fulfilled_value` jsonb + `fulfilled_at` + `fulfilled_by_user` (NULL si
 * api_key). Gated Couche 2 D11 via action_key `missing_information.fulfill`
 * (default policy seedée 9.H.5 = `agent_allowed=false` — broker JWT bypass,
 * api_key bloquée par défaut).
 */
export const FulfillMissingInformationSchema = z.object({
  fulfilled_value: FulfilledValueSchema,
});
export type FulfillMissingInformationInput = z.infer<typeof FulfillMissingInformationSchema>;

// -----------------------------------------------------------------------------
// Waive / Reopen / Supersede (Chunk 9.I.2)
// -----------------------------------------------------------------------------
//
// Décisions PO 9.I.2 :
//   Q1=bundle 3 transitions ensemble (waive + reopen + supersede)
//   Q2=supersede Phase 1 = sémantique pure (PAS de superseded_by_id) — dette P3 9.I.3
//   Q3=waive Phase 1 = audit_log only pour « qui a waived » (PAS de waived_by_user) — dette P3
//   Q4=defaults gating tous `requires_human=true, agent_allowed=false`
//   Q5=reopen body vide (pas de reopen_reason Phase 1)
//
// Transitions :
//   waive     : open|in_progress → waived (+ waiver_reason)
//   reopen    : fulfilled|waived → open (reset destructif fulfilled_*/waiver_reason)
//   supersede : open|in_progress|fulfilled|waived → superseded (semantic pure)

/**
 * Body PATCH `/v1/missing-information/:id/waive` (9.I.2).
 *
 * `waiver_reason` 1..500 chars (parité `rejection_reason` 9.G.1). Persisté
 * dans `app.missing_information.waiver_reason text`. « Qui a waived » tracé
 * via audit_log uniquement (pas de colonne `waived_by_user` Phase 1 —
 * dette P3 « MI waived_by_user column » si UI broker dédié).
 */
export const WaiveMissingInformationSchema = z.object({
  waiver_reason: z.string().min(1).max(500),
});
export type WaiveMissingInformationInput = z.infer<typeof WaiveMissingInformationSchema>;

/**
 * Body PATCH `/v1/missing-information/:id/reopen` (9.I.2).
 *
 * Phase 1 body vide (`{}`). Pas de `reopen_reason` Phase 1 — l'audit_log
 * `changes.before` capture les valeurs `fulfilled_*` / `waiver_reason`
 * effacées (forensic preserve). Si UX broker dédié émerge, ajouter
 * `reopen_reason text` ici + colonne DB en dette dédiée.
 */
export const ReopenMissingInformationSchema = z.object({});
export type ReopenMissingInformationInput = z.infer<typeof ReopenMissingInformationSchema>;

/**
 * Body PATCH `/v1/missing-information/:id/supersede` (9.I.2).
 *
 * Phase 1 body vide. **Sémantique pure** — la table `app.missing_information`
 * n'a PAS de colonne `superseded_by_id` (cf. décision DDL 0021 vs
 * `intake_events.superseded_by_id` qui en a une). Le broker juge en
 * autonomie : si une nouvelle question similaire est nécessaire, il crée
 * une nouvelle MI row séparément. Dette P3 9.I.3 « MI superseded_by_id
 * link column » si retour PO besoin réel de tracer la row de remplacement.
 */
export const SupersedeMissingInformationSchema = z.object({});
export type SupersedeMissingInformationInput = z.infer<typeof SupersedeMissingInformationSchema>;
